"use client";

import { useParams, useRouter } from "next/navigation";
import { useContext, useEffect } from "react";

import { EmptyState } from "@/components/shared/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { APP_NAME_GENERIC, APP_ROUTES } from "@/utils/constants";
import type { Dictionary } from "@/utils/helpers";
import { getUserName } from "@/utils/usernames";

export default function Error({ error }: { error: Dictionary<string> }) {
  const router = useRouter();
  const params = useParams();
  const { loading } = useContext(UserContext);

  const { toast } = useToast();

  useEffect(() => {
    // Note: set timeout to avoid SSR error - on first render, the toast is not yet available
    setTimeout(() => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened  - ${error?.message}`,
      });
    }, 0);
  }, [error, router, toast]);

  const userId = params.userId?.toString();

  if (loading) {
    return (
      <div className="m-4">
        <div className="flex gap-x-4">
          <Skeleton className="h-[310px] w-full" />
          <Skeleton className="h-[310px] w-full" />
        </div>

        <Skeleton className="mx-auto mt-8 h-10 w-[160px]" />
      </div>
    );
  }

  return (
    <div className="flex h-safe-screen items-center justify-center">
      <EmptyState
        image="/assets/empty-24.png"
        title={`The user ${getUserName({
          account: {
            username: userId,
            publicKey: userId,
          },
        })} does not exist on ${APP_NAME_GENERIC}.`}
        description={`Unfortunately, we couldn't find the user ${getUserName({
          account: {
            username: userId,
            publicKey: userId,
          },
        })} on ${APP_NAME_GENERIC}. Click the button below to find active users.`}
        buttonText="Find Active Users"
        onClick={() => {
          router.push(APP_ROUTES.discover);
        }}
      />
    </div>
  );
}
